import React, { useState, useRef, useCallback } from "react";
import { GoogleMap, useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// --- Container style ---
const containerStyle = {
  width: "100%",
  height: "300px",
  borderRadius: "12px",
};

// --- Default Center ---
const defaultCenter = { lat: 37.7749, lng: -122.4194 }; // San Francisco default

// --- Map Styles ---
const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b9a76" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
  { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#17263c" }] },
];

const lightMapStyle = [
  { featureType: "all", elementType: "all", stylers: [{ visibility: "on" }] },
];

const MapWithSearch = ({ onLocationSelect }) => {
  const [center, setCenter] = useState(defaultCenter);
  const autocompleteRef = useRef(null);
  const { t } = useTranslation();
  const mapRef = useRef(null);

  // --- Load Google Maps JS API ---
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyADkIi1SEoI4Saqlotr6n_QmQ2iVY2aFAk",
    libraries: ["places"],
  });

  // --- Redux Theme Mode ---
  const mode = useSelector((state) => state.theme.mode);
  const isDarkMode = mode === "dark";

  // --- Handle place selection ---
  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place?.geometry?.location) {
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setCenter(location);
        if (onLocationSelect) onLocationSelect(location);
      }
    }
  }, [onLocationSelect]);

  if (!isLoaded)
    return <p className="text-text-secondary text-center py-10">{t("map.loading")}</p>;

  return (
    <div className="flex flex-col gap-3 font-inter">
      {/* --- Label --- */}
      {/* <label className="text-lg font-semibold text-text-primary">
        {t("map.selectLocation")}
      </label> */}

      {/* --- Search Bar --- */}
      <Autocomplete
        onLoad={(ref) => (autocompleteRef.current = ref)}
        onPlaceChanged={onPlaceChanged}
      >
        <input
          type="text"
          placeholder={t("map.searchPlaceholder")}
            className={`w-full p-3 border rounded-xl bg-background-card text-text-primary 
              placeholder:text-text-secondary border-border focus:outline-none focus:ring-0`}
          />

        
         
      </Autocomplete>

      {/* --- Map --- */}
      <div className="relative mt-1">
        <GoogleMap
          key={isDarkMode ? "dark-map" : "light-map"}
          mapContainerStyle={containerStyle}
          center={center}
          zoom={14}
           onLoad={(map) => (mapRef.current = map)}   // <-- store map instance
  onDragEnd={() => {                        // <-- update when user drags map
    if (mapRef.current) {
      const newCenter = mapRef.current.getCenter();
      const updatedLocation = {
        lat: newCenter.lat(),
        lng: newCenter.lng(),
      };
      setCenter(updatedLocation);
      if (onLocationSelect) onLocationSelect(updatedLocation);
    }
  }}
          options={{
            styles: isDarkMode ? darkMapStyle : lightMapStyle,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        />

        {/* --- Fixed Center Pin --- */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`w-8 h-8 drop-shadow-md ${isDarkMode ? "text-accent" : "text-accent"
              }`}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
            <circle cx="12" cy="9" r="2.5" fill="white" />
          </svg>
        </div>
      </div>

      {/* --- Coordinates Display --- */}
      <p className="text-sm text-text-secondary text-center mt-2">
        {t("map.coordinates", {
          lat: center.lat.toFixed(5),
          lng: center.lng.toFixed(5),
        })}
      </p>
    </div>
  );
};

export default MapWithSearch;
